/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import _ from 'lodash';
import SVGInline from 'react-svg-inline';

import Dropdown from 'ub/components/dropdown/dropdown';

import fontServiceUtils from 'ub/fonts/font-service/utils-new';
import fontPickerModal from 'ub/fonts/font-picker/font-picker';
import FontPickerStateController from 'ub/fonts/font-picker/state-controller';
import Manager from 'ub/fonts/font-service/manager';
import AlertDialog from 'ub/ui/alert-dialog';

import buttons from 'ui/buttons';

import styles from './font-picker-font-family.scss';
import IconDelete from 'icons/actions/delete';

function _getFontNames() {
  return _.map(fontServiceUtils.allowedFonts, (name) => {
    return name.toLowerCase();
  });
}

function _getSelectedFamily(familyValue) {
  const newBaseFamily = fontServiceUtils.cleanFontString(familyValue);

  const fontNames = _getFontNames();

  const selectedFamilyIndex = _.findIndex(fontNames, (fontName) => {
    return fontName === newBaseFamily;
  });

  const firstIndex = 0;

  if (selectedFamilyIndex >= firstIndex) {
    const fontData = fontServiceUtils.allFonts[selectedFamilyIndex];

    fontData.value = fontServiceUtils.getFormattedFontFamily(fontData.value);

    return fontData;
  } else {
    return {
      label: 'Custom',
      value: fontServiceUtils.getFormattedFontFamily(familyValue)
    };
  }
}

export default class FontPickerFontFamily extends React.Component {
  constructor(props) {
    super(props);

    this.changeFontFamily = this.changeFontFamily.bind(this);
    this.removeFont = this.removeFont.bind(this);

    this.selectedFamily = _getSelectedFamily(props.selectedFontFamily);

    const webFontOptions = this.getWebFontComponents(props);
    const systemFontOptions = this.getSysFontComponents();

    this.state = {
      webFontOptions,
      systemFontOptions,
      persistOpenOnBlur: false
    };

    this.fontPickerStateOpenSubscription = FontPickerStateController.stateChangedSubject
      .map((state) => { return state.isOpen; })
      .distinctUntilChanged()
      .subscribe((isFontPickerModalOpen) => {
        this.setState({persistOpenOnBlur: isFontPickerModalOpen});
      });

    this.webfontsSubscription = Manager.fontStorageUpdates
      .ub_subscribe(() => {
        const webFontOptions = this.getWebFontComponents(this.props);

        this.setState({webFontOptions});
      });
  }

  componentWillUnmount() {
    this.fontPickerStateOpenSubscription.dispose();
    this.webfontsSubscription.dispose();
  }

  componentWillReceiveProps(nextProps) {
    this.selectedFamily = _getSelectedFamily(nextProps.selectedFontFamily);

    const systemFontOptions = this.getSysFontComponents();
    const webFontOptions = this.getWebFontComponents(nextProps);

    this.setState({
      webFontOptions,
      systemFontOptions
    });
  }

  getSysFontComponents() {
    const currentValue = this.selectedFamily.value;

    return fontServiceUtils.sysFonts.map(({value, label}) => {
      return {
        style: {fontFamily: value},
        value: value,
        label,
        selected: value === currentValue
      };
    });
  }

  removeFont(family) {
    if (Manager.isFontInUse(family)) {
      this.fontRemovalWarning(family);
    } else {
      Manager.removeFont({family: family});
      this.props.changeHandler({type: 'removeFont'});
    }
  }

  fontRemovalWarning(family) {
    this.setState({persistOpenOnBlur: true});

    const closeDialog = () => {
      window.editor.dialogManager.closeCurrentDialog();
      this.setState({persistOpenOnBlur: false});
    };

    const removeAndClose = () => {
      Manager.removeFont({family: family});
      this.props.changeHandler({type: 'removeFont'});
      closeDialog();
    };

    const actionBarItems = (
      <div className={styles['dialog-footer']}>
        <a href='#' className={`${buttons.button} push-left`} onClick={closeDialog}>
          Cancel
        </a>
        <a href='#' className={`${buttons.button} ${buttons.warn}`} onClick={removeAndClose}>
          Remove Font
        </a>
      </div>
    );

    const removalAlert = new AlertDialog({
      title: 'Removing font from list',
      closeX: true,
      clickOutsideWillClose: true,
      message: `You are about to remove ${family}, which is currently in use.`,
      secondaryMessage: 'Any text using this font will switch to the default font.',
      icon: 'error',
      actionBar: actionBarItems
    }).safeOpen();

    return removalAlert;
  }

  deleteIcon() {
    return (
      <SVGInline
        svg={IconDelete}
        width='12px'
        fill='#ffffff'
        className={`${styles.icon} action-control`}
      />
    );
  }

  getWebFontComponents(props) {
    const webFonts = Manager.addedWebFonts;
    const currentValue = this.selectedFamily.value;

    return _.map(webFonts, (font) => {
      const fontFamily = fontServiceUtils.getFormattedFontFamily(font.family);

      return {
        style: {fontFamily},
        value: font.family,
        label: font.family,
        isOpen: fontFamily === currentValue,
        subOptions: this.getFontOptions(fontFamily, font.variants, props),
        actionControlIcon: this.deleteIcon(),
        actionControl: this.removeFont,
        containerClass: styles['collapsible-option']
      };
    });
  }

  getFontOptions(fontFamily, variants, props) {
    return variants.map((variant) => {
      const fontValue = {
        fontFamily,
        fontWeight: variant.fontWeight,
        fontStyle: variant.fontStyle
      };

      const isSelected =
        fontFamily === this.selectedFamily.value &&
        variant.fontWeight === props.selectedWeight &&
        variant.fontStyle === props.selectedFontStyle;

      return {
        style: fontValue,
        selected: isSelected,
        label: variant.displayName,
      };
    });
  }

  changeFontFamily(eventData) {
    let changeData;

    if (_.isObject(eventData)) {
      changeData = eventData;
    } else {
      changeData = {
        fontFamily: eventData,
        fontWeight: 400,
        fontStyle: 'normal'
      };
    }

    this.props.changeHandler({
      type: 'fontChange',
      value: {
        family: changeData.fontFamily,
        weight: changeData.fontWeight,
        style: changeData.fontStyle
      }
    });

    this.setState({persistOpenOnBlur: false});
  }

  render() {
    return (
      <Dropdown
        type='button'
        className='font-family-dropdown'
        allowTopScroll
        persistOpenOnBlur={this.state.persistOpenOnBlur}
        changeHandler={this.changeFontFamily}
        value={this.selectedFamily.value}
        label={this.selectedFamily.label}
        options={this.state.systemFontOptions}
        collapsibleOptions={this.state.webFontOptions}
        selectedLabelStyle={{fontFamily: this.selectedFamily.value}}
        buttonClickHandler={fontPickerModal.open}
        buttonClassName={`${buttons.button} ${buttons.dropdownFooter}`}
        buttonText='Add/Remove Font'
        wide={this.props.wide}
        openRight={this.props.openRight}
      />
    );
  }
}

FontPickerFontFamily.propTypes = {
  selectedFontFamily: React.PropTypes.string,
  selectedWeight: React.PropTypes.number,
  selectedFontStyle: React.PropTypes.string,
  changeHandler: React.PropTypes.func,
  wide: React.PropTypes.bool,
  openRight: React.PropTypes.bool
};



// WEBPACK FOOTER //
// ./src/ui/inline-text/style-tab-content-components/font-picker-font-family.jsx